import { useAction } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Numeric, Tooltip } from '@blade/ui-kit'
import { unsafeUrlDialogEvent } from '@root/entities/deeplinking/unsafe-url-dialog'
import { type TwitterSocialProfile } from '@root/entities/socials/model/socials'
import { normalizeXHref } from '@root/entities/socials/model/socials-normalize'
import { useTranslation } from '@root/shared/localization'
import {
  SocialHeartIcon,
  PersonsGroupIcon,
  SocialStatsIcon,
} from '@root/shared/ui-kit'

import { ExploreDevTwitterTooltipDefaultHeading } from './explore-dev-twitter-tooltip-heading'
import { ExploreDevTwitterTooltipHeadingBanner } from './explore-dev-twitter-tooltip-heading-banner'

import type { TwitterSocialProfileStats } from '@root/entities/socials/model/socials'
import type { IconProps } from '@root/shared/ui-kit'

type ExploreDevTwitterTooltipProps = {
  twitterInfo: TwitterSocialProfile
}

export const ExploreDevTwitterTooltip = ({
  twitterInfo,
}: ExploreDevTwitterTooltipProps) => {
  const { t } = useTranslation()

  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const profileUrl = useMemo(
    () => normalizeXHref(twitterInfo.username),
    [twitterInfo.username],
  )

  const handleOpenProfile = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    openUnsafeUrlDialog([profileUrl, 'x'])
  }

  return (
    <Root>
      <Body>
        {twitterInfo.profileBannerUrl ? (
          <ExploreDevTwitterTooltipHeadingBanner
            twitterInfo={{
              ...twitterInfo,
              profileBannerUrl: twitterInfo.profileBannerUrl!,
            }}
          />
        ) : (
          <ExploreDevTwitterTooltipDefaultHeading twitterInfo={twitterInfo} />
        )}
        {twitterInfo.description ? (
          <Description>{twitterInfo.description}</Description>
        ) : undefined}
        <ProfileStats>
          {TWITTER_SOCIAL_PROFILE_STATS.map((key) => {
            const StatIcon = STAT_ICON_MAP[key]
            const value = twitterInfo.publicMetrics[key]

            return (
              <Tooltip
                key={key}
                content={t(STAT_TOOLTIP_KEY_MAP[key])}
                align="center"
              >
                <StatItem
                  href={profileUrl}
                  onClick={handleOpenProfile}
                  aria-disabled={!value}
                >
                  <StatIcon size={14} />
                  {value ? (
                    <Numeric value={value} preset="currency-amount-short" />
                  ) : (
                    <EmptyLine />
                  )}
                </StatItem>
              </Tooltip>
            )
          })}
        </ProfileStats>
      </Body>
      {twitterInfo.pastHandles && twitterInfo.pastHandles.length > 0 ? (
        <Footer>
          <FooterHeading>{t('profile.social.past-handles')}</FooterHeading>
          <Chips>
            {twitterInfo.pastHandles.map((handle) => (
              <Chip key={handle}>{handle}</Chip>
            ))}
          </Chips>
        </Footer>
      ) : undefined}
    </Root>
  )
}

const STAT_ICON_MAP: Record<TwitterSocialProfileStats, React.FC<IconProps>> = {
  favouritesCount: SocialHeartIcon,
  followersCount: PersonsGroupIcon,
  followingCount: PersonsGroupIcon,
  mediaCount: SocialStatsIcon,
}

const STAT_TOOLTIP_KEY_MAP = {
  favouritesCount: 'profile.social.twitter-stats.favoritesCount',
  followersCount: 'profile.social.twitter-stats.followersCount',
  followingCount: 'profile.social.twitter-stats.followingCount',
  mediaCount: 'profile.social.twitter-stats.mediaCount',
} as const satisfies Record<TwitterSocialProfileStats, string>

const TWITTER_SOCIAL_PROFILE_STATS: Array<TwitterSocialProfileStats> = [
  'favouritesCount',
  'followersCount',
  'followingCount',
  'mediaCount',
]

const Root = styled.div`
  display: grid;
  padding: 3px;
`

const Body = styled.div`
  display: grid;
  gap: 8px;

  border-radius: 6px;
  background: var(--background-1);
`

const Description = styled.div`
  color: var(--text-11);
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  overflow-wrap: break-word;
  word-break: break-word;
  padding: 0 8px;
`

const Footer = styled.div`
  display: grid;
  gap: 8px;
  padding: 10px;
`

const FooterHeading = styled.div`
  color: var(--text-12);

  font-size: 15px;
  font-weight: 500;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: start;
  align-items: center;
`

const Chip = styled.span`
  all: unset;
  cursor: pointer;
  color: var(--amber-9);
  background: var(--amberA-3);

  border-radius: 4px;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;

  padding: 4px;
`

const ProfileStats = styled.div`
  padding: 0 8px 8px;
  display: grid;
  grid-template-columns: repeat(4, minmax(46px, 1fr));
  gap: 3px;
`

const StatItem = styled.a`
  all: unset;
  cursor: pointer;
  background: var(--backgroundA-2);
  color: var(--text-11);

  border-radius: 4px;
  padding: 2px 5px;

  font-size: 14px;
  font-style: normal;
  font-weight: 400;

  min-width: 46px;
  height: 24px;

  display: grid;
  grid-auto-flow: column;
  place-content: center;
  align-content: center;
  align-items: center;
  justify-content: center;
  gap: 3px;

  &[aria-disabled='true'] {
    cursor: default;
    color: var(--text-8);
  }
`

const EmptyLine = styled.span`
  background: var(--backgroundA-6);

  height: 1px;
  width: 15px;

  border-radius: 2px;
`
